import { useLayoutEffect, useState } from 'react';
import useMediaQuery from '../utils/useMediaQuery';

/**
 * The top bar's real height, for anything sticky under it. Measured off `[data-topbar]` and
 * re-measured as it grows — the safe-area inset and the ACTING AS strip (top-header handoff:
 * 64px on phones, 72px otherwise). A fixed number would
 * leave a tab strip floating off the bar's border, or hiding under it.
 *
 * The fallback (before the first measure, and in tests) is the bar's nominal height.
 */
export const TOPBAR_PHONE = 64;
export const TOPBAR_DESKTOP = 72;

export default function useTopBarHeight() {
  const phone = useMediaQuery('(max-width: 639px)');
  const [h, setH] = useState(null);

  // Re-query the bar on every render instead of once on mount, and watch its
  // BORDER box rather than the default content box.
  //
  // Both halves are load-bearing, and each was silently failing on Android:
  //
  //  - One lookup at mount cannot survive the node being replaced. Whatever the
  //    reason a new header element appears, the observer stays pointed at the
  //    detached one and the height freezes at whatever it read first — forever,
  //    because the effect had no dependencies and never ran again.
  //
  //  - .pt-safe-top grows the bar by PADDING, and padding leaves the content box
  //    untouched, so a default content-box observer never fires. Android delivers
  //    its safe-area insets after first paint, so the bar goes 118 -> 153 with
  //    nothing listening.
  //
  // Together those pinned the FILTERS pill at 118 while the bar stood 153 tall,
  // dropping it 35px up into the search row. iOS applies insets before first
  // paint and pre-140 WebViews report none, which is why only a current Android
  // device showed it.
  useLayoutEffect(() => {
    // The sticky chrome can hold more than the bar: a minimized call's bar sits above it
    // (CallMiniBar). Anything placed "under the header" has to clear both, so when the
    // bar lives inside the chrome the chrome is what gets measured.
    const topbar = document.querySelector('[data-topbar]');
    const chrome = document.querySelector('[data-pr-chrome]');
    const callBar = document.querySelector('[data-call-bar]');
    const el = topbar && chrome && chrome.contains(topbar) ? chrome : topbar || callBar;
    if (!el) return undefined;
    const measure = () => setH(el.offsetHeight || null);
    measure(); // React bails out when the value is unchanged, so this can't loop
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el, { box: 'border-box' });
    return () => ro.disconnect();
  });

  return h ?? (phone ? TOPBAR_PHONE : TOPBAR_DESKTOP);
}

/**
 * The sticky chrome's whole height — the header if it is showing plus a minimized call's
 * bar — with 0 when nothing is there. For a page that drops its own header but still has
 * to make room for the call bar (an open chat on a phone).
 */
export function useChromeHeight() {
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    const el = document.querySelector('[data-pr-chrome]');
    const measure = () => setH(el ? el.offsetHeight || 0 : 0);
    measure(); // React bails out when the value is unchanged, so this can't loop
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el, { box: 'border-box' });
    return () => ro.disconnect();
  });
  return h;
}
